/**
 * Per-scoring-form measurement contracts.
 *
 * The measurement create/update request is assembled host-side in
 * `measurements.saga.ts` → `prepareRequestData`. The *shape* of `measurementData`
 * (annotations + aggregates) varies by scoring form: a cross-ruler in RANO must
 * carry `instrument: <inventory code>` (e.g. `DYNAMIKA_CROSS_RULER`) and a
 * `format` descriptor on every numeric Value, whereas the legacy default sends
 * the historic `CROSS_RULER`/`MARKER` literals with no `format`.
 *
 * This module owns that variation: a `MeasurementContract` strategy keyed by
 * scoring form code, resolved via {@link resolveMeasurementContract} with a
 * byte-identical {@link legacyDefaultContract} fallback. The saga keeps all the
 * generic plumbing (seed adoption, permanentData, UID, POST-vs-PUT).
 *
 * The pure cachedStats → BE "Value" helpers live here too (they were lifted from
 * the saga, which re-exports them for back-compat) so the value layer and the
 * contracts that consume it sit together.
 */

import type {
  ClinicalTrialInventoryInstrumentType,
  ClinicalTrialInventoryMeasurementType,
} from '@dynamika-shared/types/clinicalTrialInventory';
import {
  resolveValueFormat,
  DEFAULT_VALUE_FORMAT,
  type ValueFormat,
} from '@dynamika-shared/utils/measurements/formatValue';

// ---------------------------------------------------------------------------
// cachedStats → BE "Value" shape (small, pure, independently unit-tested).
//
// The backend expects a measured value as
//   { value, valuePrecise, valueString, units }   when the number is real, or
//   { valueString: 'N/A' }                          when it is missing.
// ---------------------------------------------------------------------------

/** Round to 2 decimals — the precision the backend stores for measured values. */
export const round2 = (n: number): number => +n.toFixed(2);

/** Build the BE measured-value object from a finite raw number. */
export const toMeasuredValue = (
  raw: number,
  units: 'mm' | 'mm2'
): Record<string, unknown> => ({
  value: round2(raw),
  valuePrecise: raw,
  valueString: round2(raw).toString(),
  units,
});

/** The BE "not available" value, used when the input is missing/non-finite. */
export const naMeasuredValue = (): Record<string, unknown> => ({ valueString: 'N/A' });

/**
 * Convert a cornerstone cachedStats number (length / width / PPD) into the BE
 * Value shape: a measured value when the input is a finite number, else N/A.
 */
export const valueFromCachedStats = (
  raw: number | null | undefined,
  units: 'mm' | 'mm2'
): Record<string, unknown> =>
  typeof raw === 'number' && Number.isFinite(raw)
    ? toMeasuredValue(raw, units)
    : naMeasuredValue();

// ---------------------------------------------------------------------------
// format layer — config-sourced display descriptor for numeric Values.
//
// The BE numeric `Value` branch requires `format { pattern, mode }`. The durable
// source is the inventory measurement config; until the backend provides it,
// `readFormat` falls back to DEFAULT_VALUE_FORMAT (documented stopgap).
//
// The validation (mode enum + pattern grammar) lives in the shared
// `resolveValueFormat` — also what stamps `ActiveInstrument.format` for the
// viewer's TargetLesionTool display, so the outbound payload and on-viewport
// text agree on what counts as a valid `Format` by construction.
// ---------------------------------------------------------------------------

export type MeasurementFormat = ValueFormat;

export { DEFAULT_VALUE_FORMAT };

/**
 * Resolve the display `format` from the measurement config, else the default.
 * The config value is validated against the BE `Format` constraints (mode enum +
 * pattern grammar) — a malformed config value falls back to the default rather
 * than forwarding something the BE would 400 on.
 */
export const readFormat = (
  measurementConfig?: ClinicalTrialInventoryMeasurementType
): MeasurementFormat => resolveValueFormat(measurementConfig?.metadata);

/** Like `toMeasuredValue`, with a `format` descriptor stamped on. */
export const toMeasuredValueWithFormat = (
  raw: number,
  units: 'mm' | 'mm2',
  format: MeasurementFormat
): Record<string, unknown> => ({ ...toMeasuredValue(raw, units), format });

/** Like `valueFromCachedStats`, stamping `format` on the numeric branch. */
export const valueFromCachedStatsWithFormat = (
  raw: number | null | undefined,
  units: 'mm' | 'mm2',
  format: MeasurementFormat
): Record<string, unknown> =>
  typeof raw === 'number' && Number.isFinite(raw)
    ? toMeasuredValueWithFormat(raw, units, format)
    : naMeasuredValue();

// ---------------------------------------------------------------------------
// Contract strategy + registry.
// ---------------------------------------------------------------------------

export type MeasurementContractInput = {
  /** Cornerstone tool name — `'TargetLesion'` | `'NonTargetLesion'` | … */
  toolName?: string;
  instrumentConfig?: ClinicalTrialInventoryInstrumentType;
  measurementConfig?: ClinicalTrialInventoryMeasurementType;
  cachedStatsFirst?: { length?: number; width?: number; unit?: string };
  annotationIndex: number;
};

export type ShapedMeasurementData = {
  annotations: Record<string, unknown>[];
  // Required: every contract emits an `sppd` aggregate (legacy always did). Keep
  // it non-optional so a future contract can't silently change the payload shape.
  sppd: Record<string, unknown>;
};

export type MeasurementContract = (input: MeasurementContractInput) => ShapedMeasurementData;

/** PPD = LAD × SAD when both axes are present; null otherwise. */
const ppdNumericOf = (s?: { length?: number; width?: number }): number | null =>
  typeof s?.length === 'number' && typeof s?.width === 'number' ? s.length * s.width : null;

/**
 * Historic shaping, lifted verbatim from the saga: hardcoded `MARKER`/
 * `CROSS_RULER` literals, no `format`. Every form without a registered contract
 * resolves here, so its outbound payload is byte-identical to before.
 */
export const legacyDefaultContract: MeasurementContract = ({
  toolName,
  cachedStatsFirst,
  annotationIndex,
}) => {
  const lad = valueFromCachedStats(cachedStatsFirst?.length, 'mm');
  const sad = valueFromCachedStats(cachedStatsFirst?.width, 'mm');
  const ppd = valueFromCachedStats(ppdNumericOf(cachedStatsFirst), 'mm2');

  const annotations =
    toolName === 'NonTargetLesion'
      ? [{ instrument: 'MARKER', index: annotationIndex }]
      : toolName === 'TargetLesion'
      ? [{ instrument: 'CROSS_RULER', index: annotationIndex, lad, sad, ppd }]
      : [];

  return { annotations, sppd: ppd };
};

/**
 * RANO contract: instrument from `instrumentConfig.code` (so it matches the BE
 * schema const, e.g. `DYNAMIKA_CROSS_RULER`), and `format` stamped on every
 * numeric Value from the measurement config. Structurally parallel to the legacy
 * contract — only the instrument source and `format` differ.
 */
export const ranoContract: MeasurementContract = ({
  toolName,
  instrumentConfig,
  measurementConfig,
  cachedStatsFirst,
  annotationIndex,
}) => {
  // Instrument code is preferred from the inventory config (REQ-2), but a
  // cornerstone-driven UPDATE of an existing lesion (drag/resize on the canvas)
  // resolves the config from `currentMeasurement.instrumentId` only, which can be
  // absent — leaving `instrumentConfig` undefined. Emitting `instrument:
  // undefined` drops the key and the BE 400s the whole measurement (D7-7393). The
  // annotation shape MUST always carry a schema-valid instrument, so fall back to
  // the RANO const the `toolName` implies (mirrors the legacy contract, which
  // hardcoded the instrument by tool). Config still wins when present.
  const instrument =
    instrumentConfig?.code ??
    (toolName === 'NonTargetLesion' ? 'DYNAMIKA_MARKER' : 'DYNAMIKA_CROSS_RULER');
  const format = readFormat(measurementConfig);
  const v = (raw: number | null | undefined, units: 'mm' | 'mm2') =>
    valueFromCachedStatsWithFormat(raw, units, format);

  const lad = v(cachedStatsFirst?.length, 'mm');
  const sad = v(cachedStatsFirst?.width, 'mm');
  const ppd = v(ppdNumericOf(cachedStatsFirst), 'mm2');

  const annotations =
    toolName === 'NonTargetLesion'
      ? [{ instrument, index: annotationIndex }]
      : toolName === 'TargetLesion'
      ? [{ instrument, index: annotationIndex, lad, sad, ppd }]
      : [];

  return { annotations, sppd: ppd };
};

/** Scoring-form-code → contract. Unlisted forms use {@link legacyDefaultContract}. */
export const MEASUREMENT_CONTRACTS: Partial<Record<string, MeasurementContract>> = {
  RANO_BASELINE_VISIT_SCORING_FORM: ranoContract,
  RANO_FOLLOWUP_VISIT_SCORING_FORM: ranoContract,
  RANO_V2_BASELINE_VISIT_SCORING_FORM: ranoContract,
  RANO_V2_FOLLOWUP_VISIT_SCORING_FORM: ranoContract,
  BASELINE_MEASURABLE_DISEASE_SCORING_FORM: ranoContract,
};

/** Resolve the contract for a scoring form code, falling back to legacy. */
export const resolveMeasurementContract = (scoringCode?: string): MeasurementContract =>
  (scoringCode && MEASUREMENT_CONTRACTS[scoringCode]) || legacyDefaultContract;
